import { type ComponentProps } from 'react'
import { type CMSPage, CMSPageError } from '@/features/cms/components/CMS/CMSPage'
import { CMSSection } from '@/features/cms/components/CMS/CMSSection'
import { NoDataState } from '@/features/cms/components/CMS/components/Layouts/NoDataState'
import { type FooterData, type Section } from '@/features/cms/components/CMS/types'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { buildCmsStructuredData } from '@/features/shared/utils/seo/buildCmsStructuredData'
import { getCanonicalUrl } from '@/features/shared/utils/seo/seoConfig'
import { usePathname } from 'next/navigation'

export type PartialCMSPageChildrenRenderProps = ({
  body,
  footer,
}: {
  body: Section | undefined
  footer: FooterData[] | undefined
}) => React.ReactNode

type Props = {
  cmsData: ComponentProps<typeof CMSPage>['cmsData']
  children?: PartialCMSPageChildrenRenderProps
}

export const PartialCMSPageWithHeaderAndAnnouncement = ({ cmsData, children }: Props) => {
  const pathname = usePathname() ?? ''
  const canonicalUrl = getCanonicalUrl(pathname)

  if (typeof cmsData !== 'object' || 'error' in cmsData) {
    return <CMSPageError statusCode={cmsData.error.status} />
  }
  if (cmsData.content.content_type_id !== 'page_layout') {
    return <NoDataState />
  }
  const { header, announcement, body, footer } = cmsData.content.data

  return (
    <>
      <SEOHead
        title={cmsData.seo_title}
        description={cmsData.seo_description}
        canonicalUrl={canonicalUrl}
        doNotIndex={cmsData.do_not_index}
        doNotFollow={cmsData.do_not_follow}
        additionalSchemas={buildCmsStructuredData({ cmsData, canonicalUrl })}
      />
      {!!announcement && <CMSSection section={announcement} />}
      {!!header && <CMSSection section={header} />}
      {children?.({ body, footer })}
    </>
  )
}
